import type {ContentReviewDto} from '@devmil/contracts';
import {
  BookOpen,
  Clapperboard,
  GalleryHorizontalEnd,
  Newspaper,
  Radar,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

import {KIND_LABEL, queueStateOf} from '../../lib/format.js';

/**
 * Ícone da miniatura, exatamente como na prévia aprovada. É escolha editorial
 * por tipo e série, e não regra derivável, então mora aqui, na camada de
 * apresentação, em vez de virar campo de contrato que a API teria de preencher.
 */
const QUEUE_ICON: Record<string, LucideIcon> = {
  'reel:impacto-operacional': Clapperboard,
  'story:impacto-operacional': Workflow,
  'story:caderno-de-automacao': BookOpen,
  'reel:radar-ia': Radar,
  // O post de foto das 12:00. Jornal, e não câmera: o que separa esta peça das
  // outras não é ser imagem, é ser NOTÍCIA. A série dela é sempre `radar-ia`,
  // então sem esta linha ela cairia no radar do Reel e as duas ficariam iguais
  // na fila.
  'photo:radar-ia': Newspaper,
  // O carrossel das 16:30. Pilha de cartões, e não caderno: a série dele é
  // sempre `caderno-de-automacao`, e sem esta linha ele cairia no ícone da
  // Story daquela série. O que separa esta peça na fila é ser SEQUÊNCIA.
  'carousel:caderno-de-automacao': GalleryHorizontalEnd,
};

const iconFor = (piece: ContentReviewDto): LucideIcon => {
  const escolhido = QUEUE_ICON[`${piece.kind}:${piece.series.id}`];
  if (escolhido) {
    return escolhido;
  }
  // A reserva, por espécie, quando a série não tem desenho próprio.
  if (piece.kind === 'photo') {
    return Newspaper;
  }
  if (piece.kind === 'carousel') {
    return GalleryHorizontalEnd;
  }
  return piece.kind === 'reel' ? Clapperboard : Workflow;
};

export const ContentQueueItem = ({
  piece,
  selected,
  onSelect,
}: {
  piece: ContentReviewDto;
  selected: boolean;
  onSelect: (contentVersionId: string) => void;
}) => {
  const Icon = iconFor(piece);
  const state = queueStateOf(piece);

  return (
    <button
      className="dm-piece"
      type="button"
      aria-pressed={selected}
      onClick={() => {
        onSelect(piece.contentVersionId);
      }}
    >
      <span className="dm-piece-thumb">
        <Icon aria-hidden="true" />
      </span>
      {/*
        A espécie escrita, e não só o ícone.

        O ícone parece bastar quando as duas linhas do bloco são uma Reel e um
        Story, mas `QUEUE_ICON` acima é EDITORIAL: `reel:radar-ia` é um radar e
        `story:caderno-de-automacao` é um livro, então o mesmo tipo muda de
        desenho conforme a série. Quem aprende o desenho aprende a série, não a
        espécie. São quatro caracteres, e são os que dizem o que vai ao ar.
      */}
      <span className="dm-piece-meta">{KIND_LABEL[piece.kind]}</span>
      {/*
        O horário e a série saíram daqui e subiram para o cabeçalho do bloco:
        eles são do ITEM, e repeti-los na Reel e no Story era escrever a mesma
        informação duas vezes em cima de um gancho que não cabia.
      */}
      <span className="dm-piece-title">{piece.title}</span>
      <span className={state.className}>{state.label}</span>
    </button>
  );
};
